import { useState, useEffect, useRef } from 'react';
import { trpc } from '../../lib/trpc';
import { CustomButton } from '../uiWrappers/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CustomRadioGroup, RadioGroupItem } from '@/components/uiWrappers/radio-group';
import { CustomCheckbox } from '../uiWrappers/checkbox';
import { Label } from '../ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { QuestionType, Question, BooleanDisplayFormat, ImageFileExtension, AudioType, Bucket } from '@mightybyte/business';
import { FiTrash2, FiX } from 'react-icons/fi';
import { PiCamera } from 'react-icons/pi';
import { cn } from '@/lib/utils';
import { ActualValuesInput, ActualValues } from './ActualValuesInput';
import { AudioUploadSection, AudioEntry } from './AudioUploadSection';
import { BucketConfigInput, ResultsProps } from './BucketConfigInput';

interface QuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  quizId: string;
  quizNumber: number;
  editingQuestion?: Question | null;
}

function resolveImageFileExtension(file: File): ImageFileExtension | null {
  switch (file.type) {
    case 'image/png':
      return ImageFileExtension.PNG;
    case 'image/jpeg':
      return ImageFileExtension.JPG;
    case 'image/webp':
      return ImageFileExtension.WEBP;
    default: {
      const loweredName = file.name.toLowerCase();
      if (loweredName.endsWith('.png')) return ImageFileExtension.PNG;
      if (loweredName.endsWith('.jpeg')) return ImageFileExtension.JPEG;
      if (loweredName.endsWith('.jpg')) return ImageFileExtension.JPG;
      if (loweredName.endsWith('.webp')) return ImageFileExtension.WEBP;
      return null;
    }
  }
}

export function QuestionModal({ isOpen, onClose, quizId, quizNumber, editingQuestion }: QuestionModalProps) {
  const [questionNumber, setQuestionNumber] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState<QuestionType>(QuestionType.Boolean);
  const [error, setError] = useState('');

  // Boolean settings
  const [booleanFormat, setBooleanFormat] = useState<BooleanDisplayFormat>(BooleanDisplayFormat.YesNo);

  // Numeric settings
  const [numericMin, setNumericMin] = useState<string>('');
  const [numericMax, setNumericMax] = useState<string>('');
  const [useStrictMax, setUseStrictMax] = useState(false);
  const [numericUnits, setNumericUnits] = useState('');

  // Single choice settings
  const [choiceOptions, setChoiceOptions] = useState<string[]>(['']);

  // Actual values
  const [actualValues, setActualValues] = useState<ActualValues | null>(null);

  // Bucket question
  const [isBucketQuestion, setIsBucketQuestion] = useState(false);
  const [resultsProps, setResultsProps] = useState<ResultsProps | null>(null);

  // Pair with result number
  const [pairWithResultNumber, setPairWithResultNumber] = useState<string>('');

  // Hide from results
  const [hideFromResults, setHideFromResults] = useState(false);

  // Audio uploads
  const [audios, setAudios] = useState<Partial<Record<AudioType, AudioEntry>> | null>(null);

  // Image upload
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadedImageFileName, setUploadedImageFileName] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const utils = trpc.useUtils();

  // Query quiz data to check for existing bucket questions
  const { data: quizData } = trpc.admin.quiz.getById.useQuery({ id: quizId }, { enabled: isOpen });

  // Get image upload URL mutation
  const getImageUploadUrlMutation = trpc.admin.quiz.getQuestionImageUploadUrl.useMutation();

  // Reset form when modal opens/closes or editingQuestion changes
  useEffect(() => {
    if (isOpen) {
      if (editingQuestion) {
        setQuestionNumber(editingQuestion.number.toString());
        setQuestionText(editingQuestion.question);
        setQuestionType(editingQuestion.type);

        // Populate answer props based on type
        if (editingQuestion.answerProps.type === QuestionType.Boolean) {
          setBooleanFormat(editingQuestion.answerProps.props.displayFormat);
        } else if (editingQuestion.answerProps.type === QuestionType.Numeric) {
          setNumericMin(String(editingQuestion.answerProps.props.min));
          setNumericMax(String(editingQuestion.answerProps.props.max));
          setUseStrictMax(editingQuestion.answerProps.props.useStrictMax ?? false);
          setNumericUnits(editingQuestion.answerProps.props.units ?? '');
        } else if (editingQuestion.answerProps.type === QuestionType.SingleChoice) {
          setChoiceOptions(editingQuestion.answerProps.props.options.length > 0
            ? editingQuestion.answerProps.props.options
            : ['']);
        }

        // Populate actual values if available
        setActualValues(editingQuestion.actualValues ? editingQuestion.actualValues as ActualValues : null);

        // Populate bucket question settings
        setIsBucketQuestion(editingQuestion.isBucketQuestion);
        setResultsProps(editingQuestion.resultsProps ? editingQuestion.resultsProps as ResultsProps : null);

        // Populate pair with result number
        setPairWithResultNumber(editingQuestion.pairWithResultNumber ? editingQuestion.pairWithResultNumber.toString() : '');

        // Populate hide from results
        setHideFromResults(editingQuestion.hideFromResults ?? false);

        // Populate audios if available
        // Backend returns {fileName, url, transcript?} objects for saved audios
        if (editingQuestion.audios) {
          const audioData: Partial<Record<AudioType, AudioEntry>> = {};
          Object.entries(editingQuestion.audios).forEach(([audioType, audioInfo]) => {
            if (audioInfo && typeof audioInfo === 'object' && 'url' in audioInfo) {
              audioData[audioType as AudioType] = {
                url: audioInfo.url,
                transcript: (audioInfo as any).transcript,
              };
            }
          });
          setAudios(Object.keys(audioData).length > 0 ? audioData : null);
        } else {
          setAudios(null);
        }

        // Set existing image if available
        if (editingQuestion.image) {
          setImagePreview(editingQuestion.image.medium);
          // Handle both full URL and just filename
          const originalImage = editingQuestion.image.original || editingQuestion.image.medium || editingQuestion.image.large;
          if (originalImage) {
            // If it's a full URL, extract filename; otherwise use as-is
            const filename = originalImage.includes('/')
              ? originalImage.split('/').pop() || ''
              : originalImage;
            setUploadedImageFileName(filename);
          }
        } else {
          setImagePreview('');
          setUploadedImageFileName('');
        }
      } else {
        setQuestionNumber('');
        setQuestionText('');
        setQuestionType(QuestionType.Boolean);

        // Reset answer props settings
        setBooleanFormat(BooleanDisplayFormat.YesNo);
        setNumericMin('');
        setNumericMax('');
        setUseStrictMax(false);
        setNumericUnits('');
        setChoiceOptions(['']);

        // Reset actual values
        setActualValues(null);

        // Reset bucket question settings
        setIsBucketQuestion(false);
        setResultsProps(null);

        // Reset pair with result number
        setPairWithResultNumber('');

        // Reset hide from results
        setHideFromResults(false);

        // Reset audios
        setAudios(null);

        // Reset image
        setImageFile(null);
        setImagePreview('');
        setUploadedImageFileName('');
      }

      setError('');
    }
  }, [isOpen, editingQuestion]);

  // Handle image file selection
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image size must be less than 50MB');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload image
    await uploadImage(file);
  };

  const uploadImage = async (file: File) => {
    try {
      setIsUploadingImage(true);

      const fileExtension = resolveImageFileExtension(file);
      if (!fileExtension) {
        toast.error('Unsupported image format. Use JPG, PNG, or WEBP.');
        return;
      }

      // Get signed upload URL
      const uploadData = await getImageUploadUrlMutation.mutateAsync({
        fileExtension,
      });

      // Upload file to signed URL
      const uploadResponse = await fetch(uploadData.signedUrl, {
        method: uploadData.requestMethod as string,
        headers: uploadData.headers as Record<string, string>,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      // Store fileName (publicUrl available in uploadData but not needed - we use local preview)
      setUploadedImageFileName(uploadData.fileName);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setImageFile(null);
      setImagePreview('');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setUploadedImageFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addQuestionMutation = trpc.admin.quiz.addQuestion.useMutation({
    onSuccess: async () => {
      await utils.admin.quiz.getById.invalidate({ id: quizId });
      // onClose();
      toast.success('Question added successfully!');
    },
    onError: (error) => {
      setError(error.message);
      toast.error(`Failed to add question: ${error.message}`);
    },
  });

  const updateQuestionMutation = trpc.admin.quiz.updateQuestion.useMutation({
    onSuccess: async () => {
      await utils.admin.quiz.getById.invalidate({ id: quizId });
      // onClose();
      toast.success('Question updated successfully!');
    },
    onError: (error) => {
      setError(error.message);
      toast.error(`Failed to update question: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const number = parseInt(questionNumber, 10);

    if (isNaN(number) || number <= 0) {
      setError('Please enter a valid positive number');
      return;
    }

    if (!questionText.trim()) {
      setError('Please enter a question');
      return;
    }

    // Build answerProps based on question type
    let answerProps;
    switch (questionType) {
      case QuestionType.Boolean:
        answerProps = {
          type: QuestionType.Boolean as const,
          props: {
            displayFormat: booleanFormat,
          },
        };
        break;
      case QuestionType.Numeric:
        const min = numericMin === '' ? undefined : Number(numericMin);
        const max = numericMax === '' ? undefined : Number(numericMax);

        if (min === undefined || max === undefined || isNaN(min) || isNaN(max)) {
          setError('Please enter valid minimum and maximum values for numeric questions');
          return;
        }
        answerProps = {
          type: QuestionType.Numeric as const,
          props: {
            min,
            max,
            useStrictMax,
            units: numericUnits || undefined,
          },
        };
        break;
      case QuestionType.Percentage:
        answerProps = {
          type: QuestionType.Percentage as const,
          props: {},
        };
        break;
      case QuestionType.SingleChoice:
        const filteredOptions = choiceOptions.filter(opt => opt.trim());
        if (filteredOptions.length === 0) {
          setError('Please enter at least one option for single choice questions');
          return;
        }
        answerProps = {
          type: QuestionType.SingleChoice as const,
          props: {
            options: filteredOptions,
          },
        };
        break;
    }

    // Validate bucket question configuration if enabled
    if (isBucketQuestion && !resultsProps) {
      setError('Please configure bucket settings for this bucket question');
      return;
    }

    // Ensure ALL Single Choice options have bucket assignments
    let normalizedResultsProps = resultsProps;
    if (isBucketQuestion && resultsProps && questionType === QuestionType.SingleChoice && answerProps.type === QuestionType.SingleChoice) {
      const allOptions = answerProps.props.options;
      const currentMap = resultsProps.type === QuestionType.SingleChoice
        ? resultsProps.props.optionToBucketMap
        : {};

      // Create complete map with defaults for missing options
      const completeMap: { [option: string]: Bucket } = {};
      allOptions.forEach(option => {
        completeMap[option] = currentMap[option] || Bucket.One;
      });

      normalizedResultsProps = {
        type: QuestionType.SingleChoice,
        props: {
          optionToBucketMap: completeMap
        }
      };
    }

    // Build audio payload: extract filename from URL and include transcript
    const audioPayload: {
      [AudioType.Correct]?: { fileName: string; transcript?: string };
      [AudioType.Incorrect]?: { fileName: string; transcript?: string };
      [AudioType.Underestimated]?: { fileName: string; transcript?: string };
      [AudioType.Overestimated]?: { fileName: string; transcript?: string };
    } = {};
    if (audios) {
      Object.entries(audios).forEach(([audioType, entry]) => {
        if (!entry) return;
        const url = entry.url;
        let fileName: string;
        if (url.startsWith('https://') || url.startsWith('http://')) {
          const urlParts = url.split('/');
          fileName = urlParts[urlParts.length - 1];
        } else {
          fileName = url;
        }
        audioPayload[audioType as AudioType] = {
          fileName,
          ...(entry.transcript ? { transcript: entry.transcript } : {}),
        };
      });
    }

    if (editingQuestion) {
      // For updates, check if we need to handle audio changes
      const hasExistingAudios = editingQuestion.audios && Object.keys(editingQuestion.audios).length > 0;
      const hasNewAudios = Object.keys(audioPayload).length > 0;

      const updatePayload: any = {
        quizId,
        questionId: editingQuestion.id,
        number,
        question: questionText,
        type: questionType,
        answerProps,
        isBucketQuestion,
        image: uploadedImageFileName || '',
      };

      if (actualValues) {
        updatePayload.actualValues = actualValues;
      }

      if (normalizedResultsProps) {
        updatePayload.resultsProps = normalizedResultsProps;
      }

      if (hasNewAudios || hasExistingAudios) {
        updatePayload.audios = audioPayload;
      }

      if (pairWithResultNumber && pairWithResultNumber.trim() !== '') {
        const pairNumber = parseInt(pairWithResultNumber, 10);
        if (!isNaN(pairNumber) && pairNumber > 0) {
          updatePayload.pairWithResultNumber = pairNumber;
        }
      }

      // Always include hideFromResults to ensure both checking and unchecking work properly
      updatePayload.hideFromResults = hideFromResults;

      updateQuestionMutation.mutate(updatePayload);
    } else {
      // For new questions, only include image, actualValues, and audios if they exist
      const addPayload: any = {
        quizId,
        number,
        question: questionText,
        type: questionType,
        answerProps,
        isBucketQuestion,
      };

      if (actualValues) {
        addPayload.actualValues = actualValues;
      }

      if (normalizedResultsProps) {
        addPayload.resultsProps = normalizedResultsProps;
      }

      if (Object.keys(audioPayload).length > 0) {
        addPayload.audios = audioPayload;
      }

      if (uploadedImageFileName) {
        addPayload.image = uploadedImageFileName;
      }

      if (pairWithResultNumber && pairWithResultNumber.trim() !== '') {
        const pairNumber = parseInt(pairWithResultNumber, 10);
        if (!isNaN(pairNumber) && pairNumber > 0) {
          addPayload.pairWithResultNumber = pairNumber;
        }
      }

      // Always include hideFromResults to ensure both checking and unchecking work properly
      addPayload.hideFromResults = hideFromResults;

      addQuestionMutation.mutate(addPayload);
    }
  };

  const handleClose = () => {
    if (!addQuestionMutation.isPending && !updateQuestionMutation.isPending) {
      onClose();
    }
  };

  // Bucket question checkbox handler
  const handleBucketQuestionChange = (checked: boolean) => {
    // Check if another question is already a bucket question
    if (checked && quizData?.quiz) {
      const existingBucketQuestion = quizData.quiz.questions.find(
        (q) => q.isBucketQuestion && (!editingQuestion || q.id !== editingQuestion.id)
      );

      if (existingBucketQuestion) {
        toast.error(`Question ${existingBucketQuestion.number} is already set as a bucket question. Only one bucket question is allowed per quiz.`);
        return;
      }
    }

    setIsBucketQuestion(checked);
    if (!checked) {
      setResultsProps(null);
    }
  };

  // Choice options handlers
  const addOption = () => {
    setChoiceOptions([...choiceOptions, '']);
  };

  const removeOption = (index: number) => {
    if (choiceOptions.length > 1) {
      setChoiceOptions(choiceOptions.filter((_, i) => i !== index));
    }
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...choiceOptions];
    newOptions[index] = value;
    setChoiceOptions(newOptions);
  };

  const isLoading = addQuestionMutation.isPending || updateQuestionMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-5xl sm:max-w-5xl w-full h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" showCloseButton={true}>
        <DialogHeader className="px-6 py-4 border-b shrink-0 rounded-t-xl">
          <DialogTitle className="text-xl font-semibold">
            {`Module ${quizNumber} ${editingQuestion ? `Question ${editingQuestion.number}` : 'Add question'}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <ScrollArea className="flex-1 overflow-auto">
            <div className="px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column */}
          <div className="space-y-4">
            <div className={'flex flex-row gap-3'}>
              {/* Question Number */}
              <div className="w-1/3">
                <Label htmlFor="questionNumber">Number</Label>
                <Input
                    id="questionNumber"
                    type="number"
                    min="1"
                    value={questionNumber}
                    onChange={(e) => {
                      setQuestionNumber(e.target.value);
                      setError('');
                    }}
                    disabled={!!editingQuestion}
                    autoFocus={!editingQuestion}
                />
              </div>

              {/* Question Type */}
              <div className="flex-1">
                <Label htmlFor="questionType">Type</Label>
                <Select
                  value={questionType}
                  onValueChange={(value) => {
                    setQuestionType(value as QuestionType);
                    setError('');
                  }}
                >
                  <SelectTrigger className="w-full h-auto min-h-10 px-3 py-2 rounded-lg focus-visible:ring-0.5 focus-visible:ring-brand">
                    <SelectValue placeholder="Select question type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={QuestionType.Boolean}>Boolean (Yes/No or True/False)</SelectItem>
                    <SelectItem value={QuestionType.Numeric}>Numeric (Number input)</SelectItem>
                    <SelectItem value={QuestionType.Percentage}>Percentage (0-100%)</SelectItem>
                    <SelectItem value={QuestionType.SingleChoice}>Single Choice (Multiple options)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>


            {/* Question Text */}
            <div>
              <Label htmlFor="questionText">Enter your question</Label>
              <textarea
                id="questionText"
                value={questionText}
                onChange={(e) => {
                  setQuestionText(e.target.value);
                  setError('');
                }}
                rows={3}
                className={cn(
                  "w-full resize-none h-auto min-h-24 px-3 py-2 border border-input rounded-lg bg-transparent",
                  "focus-visible:outline-none focus-visible:ring-0.5 focus-visible:ring-brand focus-visible:border-ring",
                  "disabled:cursor-not-allowed disabled:opacity-50 transition-colors placeholder:text-muted-foreground"
                )}
                placeholder="Enter the question text"
                autoFocus={!!editingQuestion}
              />
            </div>

            {/* Answer Options Section */}
            <div className="space-y-4">
              <h3 className="text-base font-bold text-foreground tracking-wide">Answer Options</h3>

            {questionType === QuestionType.Boolean && (
              <div>
                <Label>Display Format</Label>
                <CustomRadioGroup
                  value={booleanFormat}
                  onValueChange={(value) => setBooleanFormat(value as BooleanDisplayFormat)}
                  orientation="horizontal"
                  className="mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value={BooleanDisplayFormat.YesNo} id="yes-no" />
                    <Label htmlFor="yes-no" className="font-normal cursor-pointer">Yes/No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value={BooleanDisplayFormat.TrueFalse} id="true-false" />
                    <Label htmlFor="true-false" className="font-normal cursor-pointer">True/False</Label>
                  </div>
                </CustomRadioGroup>
              </div>
            )}

            {questionType === QuestionType.Numeric && (
              <>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label htmlFor="numericMin">Minimum</Label>
                    <Input
                      id="numericMin"
                      type="number"
                      value={numericMin}
                      onChange={(e) => setNumericMin(e.target.value)}
                      placeholder="Min value"
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="numericMax">Maximum</Label>
                    <Input
                      id="numericMax"
                      type="number"
                      value={numericMax}
                      onChange={(e) => setNumericMax(e.target.value)}
                      placeholder="Max value"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <CustomCheckbox
                    id="useStrictMax"
                    checked={useStrictMax}
                    onCheckedChange={(e)=>setUseStrictMax(!!e)}
                  />
                  <Label htmlFor="useStrictMax" className="cursor-pointer pl-2">
                    Use strict maximum
                  </Label>
                </div>

                <div>
                  <Label htmlFor="numericUnits">Units (optional)</Label>
                  <Input
                    id="numericUnits"
                    type="text"
                    value={numericUnits}
                    onChange={(e) => setNumericUnits(e.target.value)}
                    placeholder="e.g., drinks, days, hours"
                  />
                </div>
              </>
            )}

            {questionType === QuestionType.Percentage && (
              <div className="text-sm text-muted-foreground italic">
                Percentage questions automatically use 0-100% range
              </div>
            )}

            {questionType === QuestionType.SingleChoice && (
              <div>
                <Label>Options</Label>
                <div className="space-y-2">
                  {choiceOptions.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="text"
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        disabled={choiceOptions.length === 1}
                        className="p-2 text-destructive hover:text-destructive/80 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Remove option"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <CustomButton
                    type="button"
                    onClick={addOption}
                    variant="outline"
                    className="w-full"
                  >
                    + Add Option
                  </CustomButton>
                </div>
              </div>
            )}
            </div>

            {/* Image Upload */}
            <div>
              <Label htmlFor="questionImage">Image (optional)</Label>
              <div>
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Question preview"
                      className="w-full h-48 object-contain rounded-lg border-2 border-dashed border-border bg-muted p-4"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-2 hover:bg-destructive/90 shadow-md"
                      disabled={isUploadingImage}
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                    {isUploadingImage && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                        <div className="text-white text-sm font-medium">Uploading...</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="questionImage"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      disabled={isUploadingImage}
                    />
                    <Label htmlFor={'questionImage'}
                           className={cn(
                               "flex flex-col items-center justify-center w-full h-48",
                               "border-2 border-dashed border-border rounded-lg",
                               "cursor-pointer hover:bg-muted transition-colors bg-background",
                               isUploadingImage && "opacity-50 cursor-not-allowed"
                           )}
                           >
                      <PiCamera className="w-16 h-16 text-muted-foreground mb-3" />
                      <span className="text-base text-muted-foreground font-medium">
                        {isUploadingImage ? 'Uploading...' : `Upload Question Image`}
                      </span>
                      <span className="text-xs text-muted-foreground/70 mt-2">Max 50MB, JPG/PNG/WEBP</span>
                    </Label>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>

          {/* Right Column - Additional Settings */}
          <div className="space-y-4">

            {/* Actual Values Input */}
            <ActualValuesInput
              questionType={questionType}
              value={actualValues}
              onChange={setActualValues}
            />

            {/* Audio Upload Section */}
            <AudioUploadSection
              questionType={questionType}
              value={audios}
              onChange={setAudios}
            />

            {/* Bucket Question Section */}

              <BucketConfigInput
                  onBucketChecked={handleBucketQuestionChange}
                  isBucketQuestion={isBucketQuestion}
                questionType={questionType}
                value={resultsProps}
                onChange={setResultsProps}
                choiceOptions={questionType === QuestionType.SingleChoice ? choiceOptions : undefined}
              />

            {/* Results Display Section */}
            <div className="space-y-3">
              <h3 className="text-base font-bold text-foreground tracking-wide">Results Display</h3>

              <div>
                <Label htmlFor="pairWithResultNumber">Pair with Result Number (optional)</Label>
                <Input
                  id="pairWithResultNumber"
                  type="number"
                  min="1"
                  value={pairWithResultNumber}
                  onChange={(e) => setPairWithResultNumber(e.target.value)}
                  placeholder="Enter question number to pair with"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Enter the question number to display this result alongside. Both results will appear on the same screen.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <CustomCheckbox
                  id="hideFromResults"
                  checked={hideFromResults}
                  onCheckedChange={(checked) => setHideFromResults(!!checked)}
                />
                <Label htmlFor="hideFromResults" className="cursor-pointer font-normal">
                  Hide this question from quiz results
                </Label>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                When enabled, users will still answer this question during the quiz, but it won't be shown in their results.
              </p>
            </div>

          </div>
        </div>
            </div>
          </ScrollArea>

          <div className="px-6 py-4 border-t shrink-0 bg-background rounded-b-xl flex gap-3 justify-end">
            <CustomButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="default"
              disabled={isLoading || isUploadingImage}
            >
              {(isLoading || isUploadingImage)
                ? 'Saving...'
                : editingQuestion ? 'Update Question' : 'Add Question'}
            </CustomButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
